import { createFileRoute } from '@tanstack/react-router'
import { featuredCollectionsInfiniteOptions } from '@/hooks/queries/featured/collections'
import { featuredProductsInfiniteOptions } from '@/hooks/queries/featured/products'
import { InfiniteCollectionsGrid } from '@/components/collections/InfiniteCollectionsGrid'
import { InfiniteProductsGrid } from '@/components/products/InfiniteProductsGrid'
import { FeedbackButton } from '@/components/feedback/FeedbackButton'
import { useScrollPosition } from '@/hooks/useScrollPosition'
import { z } from 'zod'

const featuredSearchSchema = z.object({
    tab: z.number().min(1).max(2).default(1).catch(1),
})

export const Route = createFileRoute('/featured')({
    component: RouteComponent,
    validateSearch: featuredSearchSchema,
    loader: async ({ context: { queryClient } }) => {
        await Promise.all([
            queryClient.ensureInfiniteQueryData(
                featuredCollectionsInfiniteOptions(queryClient)
            ),
            queryClient.ensureInfiniteQueryData(
                featuredProductsInfiniteOptions(queryClient)
            ),
        ])
    },
})

function RouteComponent() {
    const { tab: activeTab } = Route.useSearch()
    const navigate = Route.useNavigate()

    const productsScroll = useScrollPosition({
        key: 'featured-products',
        enabled: activeTab === 1,
    })
    const collectionsScroll = useScrollPosition({
        key: 'featured-collections',
        enabled: activeTab === 2,
    })

    const setActiveTab = (tab: number) => {
        navigate({ search: { tab } })
    }

    return (
        <section className="mx-auto flex max-w-md flex-col py-14 md:max-w-4xl">
            <div className="fixed top-safe z-30 flex w-full max-w-md justify-center gap-4 bg-white py-4 md:top-0 md:max-w-4xl">
                <button
                    onClick={() => setActiveTab(1)}
                    className={`cursor-pointer text-sm font-medium transition-colors ${
                        activeTab === 1 ? 'text-black' : 'text-gray-400'
                    }`}
                >
                    Products
                </button>
                <button
                    onClick={() => setActiveTab(2)}
                    className={`cursor-pointer text-sm font-medium transition-colors ${
                        activeTab === 2 ? 'text-black' : 'text-gray-400'
                    }`}
                >
                    Collections
                </button>
            </div>

            <div className="relative overflow-hidden">
                <div className="mb-4 flex">
                    <div
                        ref={productsScroll.elementRef}
                        className={`h-[100dvh] w-full flex-shrink-0 touch-pan-y overflow-y-auto ${
                            activeTab === 1 ? 'block' : 'hidden'
                        }`}
                    >
                        <div className="px-2 pt-4">
                            <InfiniteProductsGrid />
                        </div>
                    </div>

                    <div
                        ref={collectionsScroll.elementRef}
                        className={`h-[100dvh] w-full flex-shrink-0 touch-pan-y overflow-y-auto ${
                            activeTab === 2 ? 'block' : 'hidden'
                        }`}
                    >
                        <div className="px-2 pt-4">
                            <InfiniteCollectionsGrid />
                        </div>
                    </div>
                </div>
            </div>

            <FeedbackButton />
        </section>
    )
}
